/**
 * src/pages/WhereDidWeComeFrom/sections/AncientMatterClosing.tsx
 * Section 6: Reflective closing "You Are Made of Ancient Matter"
 * Tying human atoms directly back to cosmic nucleosynthesis and stars.
 * Clearly labels the poetic framing as philosophy.
 */

import React from "react";
import { Sparkles, Compass } from "lucide-react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Link } from "@/components/common/Link";
import "@/components/cosmic/cosmic.css";
export function AncientMatterClosing() {
  return <section id="ancient-matter-closing" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 min-w-0" aria-label="Reflective Synthesis: Ancient Matter">
      <div className="ql-glass-strong p-6 sm:p-10 lg:p-12 rounded-2xl border border-primary/40 relative overflow-hidden flex flex-col items-center text-center">
        {/* Subtle background cosmic glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-purple-500/10 pointer-events-none" aria-hidden="true" />

        {/* Evidence badge labelling poetic framing as philosophy */}
        <div className="relative z-10 flex items-center gap-2 mb-4">
          <EvidenceBadge level="philosophy" />
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
            Cosmic Perspective
          </span>
        </div>

        <div className="relative z-10 size-12 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center text-primary mb-4 shadow-[0_0_16px_var(--glow-primary)]">
          <Sparkles className="size-6" />
        </div>

        <h2 className="relative z-10 font-serif text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight max-w-2xl leading-tight mb-4">
          You Are Made of Ancient Matter
        </h2>

        <p className="relative z-10 font-sans text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-6">
          Every hydrogen atom in the water flowing through your veins was forged in the first three minutes of the Big Bang, 13.8 billion years ago. Every carbon atom in your DNA, every calcium atom in your bones, and every iron atom carrying oxygen through your blood was synthesized in the nuclear furnaces of ancient stars and violent supernova explosions.
        </p>

        <p className="relative z-10 font-sans text-xs sm:text-sm text-foreground/80 max-w-xl leading-relaxed mb-8">
          In the most literal, empirical sense: you are not separate from the cosmos looking in. You are a portion of the universe that has developed the capacity to look back, measure its own age, and ask where it came from.
        </p>

        <div className="relative z-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/OriginAndBeyondPage" className="ql-btn">
            <Compass className="size-4" />
            <span>Return to Expedition Hub</span>
          </Link>
          <Link to="/WhatIsConsciousnessPage" className="ql-btn-ghost">
            <span>Next: What Is Consciousness? →</span>
          </Link>
        </div>
      </div>
    </section>;
}